import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Flame } from "lucide-react";
import { content } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

// Per-item placeholder images. We pick the most relevant uploaded asset per category;
// items without a custom image fall back to the category-level image.
const PUBLIC = process.env.PUBLIC_URL || "";

const CATEGORY_IMAGES = {
  tacos: `${PUBLIC}/brand/cat-tacos.jpg`,
  bowls: `${PUBLIC}/brand/cat-bowl.jpg`,
  galettes: `${PUBLIC}/brand/food-kebab-menu.jpg`,
  sandwiches: `${PUBLIC}/brand/cat-sandwich.jpg`,
  box: `${PUBLIC}/brand/cat-box.jpg`,
  drinks: `${PUBLIC}/brand/drink-coca.jpg`,
};

// Per-item images (optional). When null/undefined, falls back to the category image.
const ITEM_IMAGES = {
  // Galettes: kebab-with-fries shot for the Galette Kebab item
  "Galette Kebab": `${PUBLIC}/brand/food-kebab-menu.jpg`,
  // Drinks (FR + EN names share where applicable)
  "Coca Cola": `${PUBLIC}/brand/drink-coca.jpg`,
  "Coca Zero": `${PUBLIC}/brand/drink-coca-zero.jpg`,
  Fanta: `${PUBLIC}/brand/drink-fanta.png`,
  Sprite: `${PUBLIC}/brand/drink-sprite.jpg`,
  "Red Bull": `${PUBLIC}/brand/drink-redbull.jpg`,
  "Ice Tea": `${PUBLIC}/brand/drink-icetea.jpg`,
  Ayran: `${PUBLIC}/brand/drink-ayran.jpg`,
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut", delay: i * 0.05 },
  }),
};

const MenuSection = () => {
  const { lang } = useLang();
  const fr = content.menu.fr;
  const en = content.menu.en;
  const isBoth = lang === "both";
  const data = lang === "en" ? en : fr;

  const cats = data.categories;
  const [activeId, setActiveId] = useState(cats[0].id);
  const activeIdx = cats.findIndex((c) => c.id === activeId);
  const active = cats[activeIdx] || cats[0];
  const altActive = isBoth ? en.categories[activeIdx] : null;

  const fallbackImg = CATEGORY_IMAGES[active.id];

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-white section"
    >
      <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-3xl">
            <span className="eyebrow-yellow" data-testid="menu-eyebrow">
              <Flame size={12} strokeWidth={2.5} />
              <span>{isBoth ? `${fr.eyebrow} · ${en.eyebrow}` : data.eyebrow}</span>
            </span>
            <h2
              data-testid="menu-title"
              className="mt-5 h-display text-black text-[36px] sm:text-[48px] md:text-[60px] lg:text-[72px] leading-[0.95]"
            >
              <span className="block">{data.title}</span>
              <span className="block">
                <span className="inline-block italic mt-1" style={{
                  background: "linear-gradient(transparent 60%, var(--bb-yellow) 60%)",
                  padding: "0 0.15em",
                }}>
                  {data.titleAccent}
                </span>
              </span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 max-w-2xl leading-relaxed">
              {data.sub}
            </p>
          </div>

          <a
            href={content.externalLinks.order}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-view-full-cta"
            className="bb-btn bb-btn-yellow self-start md:self-end"
          >
            <span>{isBoth ? `${fr.cta} · ${en.cta}` : data.cta}</span>
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>

        {/* Category nav */}
        <div
          className="flex gap-3 overflow-x-auto pb-3 no-scrollbar"
          data-testid="menu-tabs"
          role="tablist"
        >
          {cats.map((c, i) => (
            <button
              key={c.id}
              type="button"
              data-testid={`menu-tab-${c.id}`}
              role="tab"
              aria-selected={c.id === activeId}
              onClick={() => setActiveId(c.id)}
              className={`menu-cat-pill ${c.id === activeId ? "active" : ""}`}
            >
              <span className="mr-1.5">{c.icon}</span>
              <span>{c.name}</span>
              {isBoth && en.categories[i].name !== c.name && (
                <span className="opacity-60 ml-1">/ {en.categories[i].name}</span>
              )}
            </button>
          ))}
        </div>

        {/* Cards grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id + lang}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-8 md:mt-12"
            data-testid={`menu-items-${active.id}`}
          >
            <div className="mb-6 md:mb-8 flex items-baseline gap-3">
              <h3 className="font-display text-2xl md:text-3xl uppercase text-black">
                {active.icon} {active.name}
              </h3>
              {isBoth && altActive && altActive.name !== active.name && (
                <span className="font-display italic text-lg md:text-xl text-black/55">
                  / {altActive.name}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {active.items.map((item, i) => {
                const img = ITEM_IMAGES[item.name] || fallbackImg;
                const altItem =
                  isBoth && altActive && altActive.items[i] ? altActive.items[i] : null;
                return (
                  <motion.article
                    key={i}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-40px" }}
                    custom={i}
                    data-testid={`menu-item-${active.id}-${i}`}
                    className="menu-card"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={img}
                        alt={item.name}
                        className="menu-card-img"
                        loading="lazy"
                      />
                    </div>
                    <div className="menu-card-body">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="menu-card-name">{item.name}</div>
                          {altItem && altItem.name !== item.name && (
                            <div className="font-body italic text-sm text-black/50 mt-0.5">
                              / {altItem.name}
                            </div>
                          )}
                        </div>
                        <span className="menu-card-price">
                          {item.price} {data.currency}
                        </span>
                      </div>
                      {item.note && (
                        <div className="menu-card-note">
                          {item.note}
                          {altItem && altItem.note && altItem.note !== item.note && (
                            <div className="italic text-black/40 mt-0.5">{altItem.note}</div>
                          )}
                        </div>
                      )}
                      <a
                        href={content.externalLinks.order}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`order-${active.id}-${i}-cta`}
                        className="bb-btn bb-btn-outline bb-btn-sm mt-5 self-start"
                      >
                        <span>{lang === "en" ? "Order" : "Commander"}</span>
                        <ArrowUpRight size={12} strokeWidth={2.5} />
                      </a>
                    </div>
                  </motion.article>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-black/60">
                {lang === "en"
                  ? "Prices in CHF · Allergens on request"
                  : "Prix en CHF · Allergènes sur demande"}
              </p>
              <a
                href={content.externalLinks.order}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`order-${active.id}-cta`}
                className="bb-btn bb-btn-black"
              >
                <span>{lang === "en" ? "Order this category" : "Commander cette catégorie"}</span>
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default MenuSection;
